/**
 * Shared labels and types for custom slides: the slide library, the
 * placement control on the Game Night hub, and the host panel outline.
 */
import api from './api';
import type { CustomSlideKind, SlideAnchor } from './slideSequence';

/** A slide in the org's library (GET /api/slides/templates). */
export interface SlideTemplate {
  id: string;
  name: string;
  kind: CustomSlideKind;
  title: string | null;
  body: string | null;
  imageUrl: string | null;
  url: string | null;
  caption: string | null;
  starterKey: string | null;
  _count?: { placements: number };
}

/** A built-in starter (GET /api/slides/starters). */
export interface StarterTemplate {
  key: string;
  name: string;
  kind: CustomSlideKind;
  title: string | null;
  body: string | null;
  caption?: string | null;
  url?: string | null;
}

export const KIND_LABELS: Record<CustomSlideKind, string> = {
  IMAGE: 'Image / sponsor',
  TEXT: 'Announcement',
  SCHEDULE: 'Next week schedule',
  PROMO: 'Promo with QR code',
};

export const KIND_HELP: Record<CustomSlideKind, string> = {
  IMAGE: 'A full-screen image, such as a sponsor logo or a flyer.',
  TEXT: 'A title and a short message, such as a house reminder.',
  SCHEDULE: 'Upcoming nights at this venue, read from your public schedule.',
  PROMO: 'A QR code for a link, with a caption. Good for bookings and socials.',
};

export const ANCHOR_OPTIONS: { value: SlideAnchor; label: string; needsRound: boolean }[] = [
  { value: 'START', label: 'At the start, after the rules', needsRound: false },
  { value: 'BEFORE_ROUND', label: 'Before a round', needsRound: true },
  { value: 'AFTER_ROUND', label: 'After a round', needsRound: true },
  { value: 'HALFTIME', label: 'At halftime', needsRound: false },
  { value: 'END', label: 'At the end, before the outro', needsRound: false },
];

/** The name a host sees for a slide in lists and in the host panel. */
export function customSlideLabel(c: { title?: string | null; name?: string | null; kind: CustomSlideKind }): string {
  return c.title?.trim() || c.name?.trim() || KIND_LABELS[c.kind];
}

/** Where a placement sits, in words. */
export function placementLabel(p: { anchor: SlideAnchor; roundNumber: number | null; fellBack?: boolean }): string {
  const base = (() => {
    switch (p.anchor) {
      case 'START': return 'At the start';
      case 'BEFORE_ROUND': return `Before round ${p.roundNumber ?? '?'}`;
      case 'AFTER_ROUND': return `After round ${p.roundNumber ?? '?'}`;
      case 'HALFTIME': return 'At halftime';
      case 'END': return 'At the end';
      default: return '';
    }
  })();
  return p.fellBack ? `${base} (no such round, shows at the end)` : base;
}

// ── API helpers ───────────────────────────────────────────────────────────

export async function fetchSlideTemplates(): Promise<SlideTemplate[]> {
  const res = await api.get('/api/slides/templates');
  return res.data?.data?.templates ?? [];
}

export async function fetchStarterTemplates(): Promise<StarterTemplate[]> {
  const res = await api.get('/api/slides/starters');
  return res.data?.data?.starters ?? [];
}

/** Upload an image through the shared upload flow and return its URL. */
export async function uploadSlideImage(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/api/uploads/image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data.url as string;
}
